/**
 * Job Types
 * Unified type definitions for all job types
 *
 * ARCHITECTURE:
 * - Single JobType enum contains ALL job types (standard + advanced)
 * - Category determines UI grouping (standard vs advanced)
 * - Status determines availability (active, coming_soon, beta)
 * - isConfigBased determines input method (file upload vs config form)
 */

// ============================================================================
// JOB TYPE ENUM - Single source of truth for all job types
// ============================================================================

export enum JobType {
  // Standard Analysis Types (internal processing)
  PEOPLE_COUNTING = "people_counting",
  NUMBER_PLATE_DETECTION = "number_plate_detection",
  PARKING_ANALYSIS = "parking_analysis",
  IN_OUT_COUNTING = "in_out_counting",
  POTHOLE_DETECTION = "pothole_detection",
  FOOD_WASTE_ESTIMATION = "food_waste_estimation",
  ROOM_READINESS = "room_readiness",
  LOBBY_CROWD_DETECTION = "lobby_crowd_detection",
  WILDLIFE_DETECTION = "wildlife_detection",

  // Advanced Analysis Types (external services)
  DAMAGE_DETECTION_3D = "damage_detection_3d",
  PPE_DETECTION = "ppe_detection",
  LIDAR_DATA = "lidar_data",
  RADAR_DATA = "radar_data",
  RGB_CAMERA = "rgb_camera",
  THERMAL_CAMERA = "thermal_camera",
  MEDICAL_IMAGING = "medical_imaging",
  BORESCOPIC_CAMERA = "borescopic_camera",
  PRODUCT_DESIGN_ENGINEERING = "product_design_engineering",
  SATELLITE_IMAGERY = "satellite_imagery",

  // Config-based Analysis Types (advanced subcategory)
  MULTI_CAMERA_SYSTEM = "multi_camera_system",
}

// ============================================================================
// JOB STATUS ENUM
// ============================================================================

export enum JobStatus {
  PENDING = "pending",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
}

// ============================================================================
// CAMERA CONFIG TYPES (for Multi-Camera System)
// ============================================================================

export interface CameraConfig {
  id: string;
  ip: string;
  location: string;
  description?: string;
}

export interface MultiCameraJobConfig {
  cameras: CameraConfig[];
}

// ============================================================================
// JOB REQUEST/RESPONSE TYPES
// ============================================================================

export interface JobCreateRequest {
  job_type: JobType;
  file?: File;
  config?: MultiCameraJobConfig;
}

export interface Job {
  id: string;
  job_number: number;
  user_id: string;
  job_type: JobType;
  status: JobStatus;
  input_file_path: string;
  file_url: string;
  annotated_output_url: string;
  results: JobResults | null;
  is_live_demo: boolean;
  error_message: string | null;
  submitted_at: string;
  started_at: string | null;
  completed_at: string | null;
  config?: MultiCameraJobConfig | null;
  // Job tracking fields
  created_via: "chat" | "manual";
  llm_provider?: string | null;
  llm_model?: string | null;
  llm_config_id?: string | null;
  chat_session_id?: string | null;
}

// ============================================================================
// JOB RESULTS - Union type for all analysis results
// ============================================================================

export type JobResults =
  | PeopleCountingResults
  | PlateDetectionResults
  | ParkingAnalysisResults
  | InOutCountingResults
  | PotholeDetectionResults
  | FoodWasteResults
  | RoomReadinessResults
  | LobbyAlertResults
  | WildlifeDetectionResults
  | DamageDetection3DResults
  | MultiCameraResults
  | PPEDetectionResults
  | PPEDetectionVideoResults;

// ============================================================================
// STANDARD RESULTS TYPES
// ============================================================================

export interface PeopleCountingResults {
  type: "video" | "image";
  total_frames_processed?: number;
  average_people_count?: number;
  max_people_count?: number;
  min_people_count?: number;
  people_count?: number;
  video_duration_seconds?: number;
  video_fps?: number;
  video_resolution?: string;
  detections?: Array<{
    confidence: number;
    bbox: number[];
  }>;
}

export interface PlateDetectionResults {
  type: "video" | "image";
  total_plates_detected?: number;
  unique_plates?: number;
  plates_detected?: number;
  vehicles_detected?: number;
  plate_frequency?: Record<string, number>;
  unique_plate_list?: Array<{
    plate_number: string;
    confidence: number;
    frame: number;
    timestamp_seconds: number;
    vehicle_type: string;
  }>;
  plates?: Array<{
    plate_number: string;
    confidence: number;
    vehicle_type: string;
    vehicle_bbox: number[];
    vehicle_confidence: number;
  }>;
}

export interface ParkingAnalysisResults {
  type: "video" | "image";
  estimated_parking_capacity?: number;
  average_vehicles?: number;
  max_vehicles?: number;
  min_vehicles?: number;
  total_vehicles_detected?: number;
  average_occupancy_rate?: number;
  vehicle_type_distribution: Record<string, number>;
  detected_vehicles?: Array<{
    vehicle_type: string;
    confidence: number;
    bbox: number[];
  }>;
}

export interface InOutCountingResults {
  type: "video";
  total_entries: number;
  total_exits: number;
  net_count: number;
  entry_rate_per_minute: number;
  exit_rate_per_minute: number;
  video_duration_seconds: number;
  video_fps: number;
  line_position: string;
  line_direction: string;
}

export interface PotholeDetectionResults {
  type: "video" | "image";
  total_potholes_detected?: number;
  potholes_detected?: number;
  unique_potholes_count?: number;
  severity_distribution: {
    minor: number;
    moderate: number;
    severe: number;
  };
  prioritized_potholes?: Array<{
    id: number;
    bbox: number[];
    severity: string;
    size_category: string;
    priority_score: number;
  }>;
  potholes?: Array<{
    id: number;
    bbox: number[];
    severity: string;
    size_category: string;
    confidence: number;
  }>;
}

export interface FoodWasteResults {
  type: "video" | "image";
  total_waste_kg: number;
  total_waste_grams: number;
  total_items_detected?: number;
  items_detected?: number;
  estimated_cost_usd: number;
  category_breakdown_kg: Record<string, number>;
  food_type_breakdown_kg: Record<string, number>;
  detected_items?: Array<{
    food_name: string;
    category: string;
    weight_grams: number;
    weight_kg: number;
    confidence: number;
    bbox: number[];
  }>;
}

export interface RoomReadinessResults {
  type: "video" | "image";
  room_ready: boolean;
  readiness_score: number;
  room_occupied: boolean;
  checklist: Record<string, any>;
  issues_found: string[];
  detected_objects: Array<{
    object: string;
    count: number;
    avg_confidence: number;
  }>;
  ready_percentage?: number;
  average_score?: number;
}

export interface LobbyAlertResults {
  type: "video" | "image";
  average_people_count?: number;
  max_people_count?: number;
  min_people_count?: number;
  people_count?: number;
  overall_density_level?: string;
  density_level?: string;
  density_ratio?: number;
  density_distribution?: Record<string, number>;
}

export interface WildlifeDetectionResults {
  type: "video" | "image";
  total_animals_detected?: number;
  animals_detected?: number;
  unique_species_count?: number;
  species_detected: Record<string, number>;
  average_animals_per_frame?: number;
  max_animals_in_single_frame?: number;
  detections?: Array<{
    species: string;
    size_category: string;
    confidence: number;
    bbox: number[];
  }>;
}

// ============================================================================
// ADVANCED RESULTS TYPES
// ============================================================================

/**
 * 3D Damage Detection Results
 * Matches the unified backend schema
 */
export interface DamageDetection3DResults {
  type: "3d_damage_detection";
  total_dents_found: number;
  damage_severity_distribution: {
    minor: number;
    moderate: number;
    severe: number;
    critical: number;
  };
  processing_time_seconds: number;
  request_id: string;
  residual_image_before: string;
  segmentation_image_after: string;
  dents: Array<{
    dent_id: number;
    location: { x: number; y: number; z: number };
    dimensions: { width: number; height: number; bbox_depth: number };
    depth_mm: number;
    depth_microns: number;
    dent_type: "outside_in" | "inside_out";
    num_vertices: number;
    severity: "minor" | "moderate" | "severe" | "critical";
  }>;
}

/**
 * Multi-Camera System Results
 */
export interface MultiCameraResults {
  type: "multi_camera";
  total_cameras: number;
  active_cameras: number;
  inactive_cameras: number;
  system_status: "active" | "partial" | "inactive";
  cameras: Array<{
    id: string;
    ip: string;
    location: string;
    description?: string;
    status: "active" | "inactive" | "error";
    stream_url?: string;
    thumbnail_url?: string;
    error_message?: string;
    last_frame_at?: string;
  }>;
}

/**
 * PPE Detection Results
 */
export interface PPEItem {
  label: string;
  confidence: number;
  xyxy: number[];
}
export interface PersonDetection {
  person_box: number[];
  person_confidence: number;
  ppe_items: PPEItem[];
  wearing_required: Record<string, boolean>;
  status: "PROTECTED" | "UNPROTECTED";
}
export interface PPEDetectionResults {
  type: "ppe_detection";
  request_id: string;
  detections: PersonDetection[];
  metrics: {
    num_persons: number;
    num_detections: number;
    total_sec: number;
  };
  summary: {
    total_persons: number;
    protected_count: number;
    unprotected_count: number;
  };
  processing_time: number;
  image_base64?: string;
}

/**
 * PPE Detection Video Results
 */
export interface PPEVideoFrameDetection {
  frame: number;
  num_persons: number;
  num_detections: number;
}

export interface PPEVideoMetadata {
  original_filename?: string;
  upload_size?: number;
  frames: number;
  processing_seconds: number;
}

export interface PPEDetectionVideoResults {
  type: "ppe_detection_video";
  request_id: string;
  metadata: PPEVideoMetadata;
  summary: {
    total_frames: number;
    total_persons_detected: number;
    total_detections: number;
  };
  frames: PPEVideoFrameDetection[];
  processing_time: number;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface JobListResponse {
  total: number;
  jobs: Job[];
  page: number;
  page_size: number;
}

export interface JobStatsResponse {
  total_jobs: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  by_type: Record<string, number>;
}

// ============================================================================
// STATE TYPES
// ============================================================================
export interface JobsState {
  jobs: Job[];
  currentJob: Job | null;
  stats: JobStatsResponse | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    page_size: number;
    total: number;
  };
  filters: {
    status: JobStatus | null;
    type: JobType | null;
  };
}

export interface AnalysisResult {
  success: boolean;
  job_type: string;
  results: {
    // Common fields (most job types)
    count?: number;

    // People counting, In/Out, Lobby crowd
    detections?: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
      confidence: number;
    }>;

    // Number plate detection
    plates?: Array<{
      text: string;
      confidence: number;
      bbox: number[];
    }>;

    // Parking analysis
    vehicle_type_distribution?: Record<string, number>;
    detected_vehicles?: Array<{
      vehicle_type: string;
      confidence: number;
      bbox: number[];
    }>;

    // Food waste estimation
    total_waste_grams?: number;
    total_waste_kg?: number;
    detected_items?: Array<{
      food_name: string;
      weight_grams: number;
      confidence: number;
      bbox: number[];
    }>;

    // Room readiness
    room_ready?: boolean;
    readiness_score?: number;
    room_occupied?: boolean;
    issues_found?: string[];
    detected_objects?: any[];

    // Lobby crowd detection
    density_level?: "empty" | "low" | "medium" | "high";

    // In/Out counting
    note?: string;

    // Allow any additional fields from backend
    [key: string]: any;
  };
  timestamp: string;
}
